import { Network } from '@capacitor/network';
import { storePosition, getUnsynced, markAsSynced } from './db';
import { supabase } from '../lib/supabase';

let isOnline = true;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const CONNECTIVITY_CHECK_TIMEOUT = 60; // 60 seconds
const NETWORK_TIMEOUT = 10000; // 10 seconds

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const retryOperation = async <T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = RETRY_DELAY
): Promise<T> => {
  try {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Network timeout')), NETWORK_TIMEOUT);
    });
    const operationPromise = operation();
    return await Promise.race([operationPromise, timeoutPromise]) as T;
  } catch (error) {
    if (retries > 0) {
      await sleep(delay);
      return retryOperation(operation, retries - 1, delay * 2);
    }
    throw error;
  }
};

export const initNetworkListener = () => {
  let retryTimeout: NodeJS.Timeout | null = null;

  Network.addListener('networkStatusChange', status => {
    const wasOffline = !isOnline;
    isOnline = status.connected;
    
    if (isOnline && wasOffline) {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      
      retryTimeout = setTimeout(() => {
        syncOfflineData().catch(error => {
          console.error('Failed to sync offline data:', error);
        });
      }, 1000);
    }
  });
  
  Network.getStatus().then(status => {
    isOnline = status.connected;
    if (isOnline) {
      syncOfflineData().catch(error => {
        console.error('Failed to sync offline data:', error);
      });
    }
  });
};

export const updateDriverPosition = async (
  driverId: string,
  position: { lat: number; lng: number }
) => {
  try {
    await storePosition({ driverId, ...position });

    if (isOnline) {
      await retryOperation(async () => {
        const { error: positionError } = await supabase
          .from('drivers')
          .update({
            position_lat: position.lat,
            position_lng: position.lng,
            last_position_update: new Date().toISOString()
          })
          .eq('id', driverId);

        if (positionError) throw positionError;
      });
    }
  } catch (error) {
    console.warn('Failed to update position:', error);
  }
};

export const syncOfflineData = async () => {
  if (!isOnline) return;
  
  try {
    const unsynced = await getUnsynced();
    
    for (const position of unsynced) {
      try {
        await retryOperation(async () => {
          const { error: positionError } = await supabase
            .from('drivers')
            .update({
              position_lat: position.lat,
              position_lng: position.lng,
              last_position_update: new Date(position.timestamp).toISOString()
            })
            .eq('id', position.driverId);

          if (positionError) throw positionError;
          await markAsSynced(position.timestamp, position.driverId);
        });
      } catch (e) {
        console.warn('Failed to sync position:', e);
      }
    }
  } catch (error) {
    console.error('Error during offline data sync:', error);
  }
};

export const checkDriverConnectivity = async (driverId: string): Promise<boolean> => {
  try {
    const { data, error } = await retryOperation(async () => {
      const response = await supabase
        .from('drivers')
        .select('is_active, last_position_update')
        .eq('id', driverId)
        .single();

      if (response.error) {
        // Driver hasn't updated position recently, but we don't automatically deactivate
        console.warn(`Driver ${driverId} hasn't updated position for ${diffSeconds} seconds`);
        throw response.error;
      }

      return response;
    });

    if (error) {
      console.warn('Error checking driver status:', error);
      return false;
    }

    if (!data || !data.is_active) {
      return false;
    }

    const lastUpdate = new Date(data.last_position_update);
    const now = new Date();
    const diffSeconds = (now.getTime() - lastUpdate.getTime()) / 1000;

    if (diffSeconds > CONNECTIVITY_CHECK_TIMEOUT) {
      try {
        await retryOperation(async () => {
          const { error: updateError } = await supabase
            .from('drivers')
            .update({ 
              is_active: false,
              position_lat: 44.260571,
              position_lng: 0.960050
            })
            .eq('id', driverId);

          if (updateError) throw updateError;
        });
      } catch (updateError) {
        console.warn('Error updating driver status:', updateError);
      }
      return false;
    }

    return true;
  } catch (e) {
    console.warn('Error checking driver connectivity:', e);
    return false;
  }
};